
import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Loading } from "../../../componentes/loading/loading";

const DashboardLineChart = ({ relatorio = {}, loading = false, setTendencia, ano, onBarClick }) => {
  const mesesMap = [
    "JANEIRO", "FEVEREIRO", "MARCO", "ABRIL", "MAIO", "JUNHO",
    "JULHO", "AGOSTO", "SETEMBRO", "OUTUBRO", "NOVEMBRO", "DEZEMBRO",
  ];

    const [ctrlPressionado, setCtrlPressionado] = useState(false);
  
    useEffect(() => {
      const keyDown = (e) => setCtrlPressionado(e.ctrlKey);
      const keyUp = (e) => setCtrlPressionado(e.ctrlKey);
  
      window.addEventListener("keydown", keyDown);
      window.addEventListener("keyup", keyUp);
  
      return () => {
        window.removeEventListener("keydown", keyDown);
        window.removeEventListener("keyup", keyUp);
      };
    }, []);

  const orcadoMensal = relatorio.totalOrcadoMensal || {};
  const realizadoMensal = relatorio.totalRealizadoMensal || {};
  const realizadoMensalAnterior = relatorio.totalRealizadoMensalAnterior || {};

  const dataOrcado = mesesMap.map((mes) => orcadoMensal[mes] || 0);
  const dataRealizado = mesesMap.map((_, idx) => realizadoMensal[idx + 1] || 0);
  const dataRealizadoAnterior = mesesMap.map((_, idx) => realizadoMensalAnterior[idx + 1] || 0);

  const todosZerados = [...dataOrcado, ...dataRealizado].every((valor) => valor === 0);

  // === CÁLCULO DA LINHA DE TENDÊNCIA (apenas cálculo; sem setState aqui) ===
  const tendenciaSerie = useMemo(() => {
    const mesAtual = new Date().getMonth(); // 0..11
    // dados até o mês anterior ao atual (se mesAtual=0 em janeiro, resultado vazio)
    const realizadoAteMesAnterior = dataRealizado.slice(0, mesAtual);

    if (realizadoAteMesAnterior.length <= 1) {
      return []; // insuficiente para regressão
    }

    const x = realizadoAteMesAnterior.map((_, i) => i + 1); // 1..n
    const y = realizadoAteMesAnterior;

    const n = x.length;
    const somaX = x.reduce((a, b) => a + b, 0);
    const somaY = y.reduce((a, b) => a + b, 0);
    const somaXY = x.reduce((acc, xi, i) => acc + xi * y[i], 0);
    const somaX2 = x.reduce((acc, xi) => acc + xi * xi, 0);

    const denom = n * somaX2 - somaX * somaX;
    if (denom === 0) return [];

    const m = (n * somaXY - somaX * somaY) / denom;
    const b = (somaY - m * somaX) / n;

    // Projeta a linha para todos os meses
    return mesesMap.map((_, i) => m * (i + 1) + b);
  }, [dataRealizado]);

  // === Atualiza a tendência no pai após render (evita warning do React) ===
  useEffect(() => {
    if (typeof setTendencia === "function" && tendenciaSerie.length > 0) {
      const total = tendenciaSerie.reduce((a, b) => a + (b || 0), 0);
      setTendencia(Number.isFinite(total) ? total : 0);
    }
  }, [tendenciaSerie, setTendencia]);

  const temAnoAnterior = Array.isArray(ano) && ano.some((a) => a < 2026);
  const somente2026 = Array.isArray(ano) && ano.length === 1 && ano.includes(2026);

  const series = [
    { name: "Orçado", data: dataOrcado },
    { name: "Realizado", data: dataRealizado },
    ...(somente2026 && !temAnoAnterior
      ? [{ name: "Realizado em 2025", data: dataRealizadoAnterior }]
      : []),
    // Se quiser exibir a tendência no gráfico, descomente a linha abaixo:
    // ...(tendenciaSerie.length ? [{ name: "Tendência", data: tendenciaSerie }] : []),
  ];

  const options = {
    chart: {
      type: "line",
      toolbar: { show: true },
      animations: {
        enabled: false
      },
      events: onBarClick
        ? {
          dataPointSelection: (_event, _chartContext, config) => {
            const mes = config.dataPointIndex + 1; // Janeiro=1 ... Dezembro=12
            onBarClick(mes);
          },
        }
        : undefined,
    },

    stroke: {
      curve: "smooth",
      width: 3,
      // Se adicionar tendência, ajuste o dashArray conforme posição:
      // Ex.: 3 séries normais + 1 tendência tracejada => [0, 0, 0, 6]
      dashArray: [0, 0, 6],
    },
    markers: {
      size: 5,
      hover: { size: 9 },
    },
    colors: ["#271de0", "#dd5555", "#ff9900", "#00b894"],
    xaxis: {
      categories: mesesMap,
    },
    yaxis: {
      tickAmount: 10,
      labels: {
        formatter: (value) =>
          new Intl.NumberFormat("pt-BR", {
            style: "currency",
            currency: "BRL",
            maximumFractionDigits: 0,
          }).format(value),
      },
    },
    grid: {
      show: true,
      borderColor: "#e0e0e0",
      strokeDashArray: 1,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    tooltip: {
      intersect: ctrlPressionado,
      shared: !ctrlPressionado,
      y: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const dadosSerie = w?.config?.series?.[seriesIndex]?.data ?? [];
          const acumulado = dadosSerie
            .slice(0, dataPointIndex + 1)
            .reduce((a, b) => a + (b || 0), 0);

          const valorFormatado = value?.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });

          const acumuladoFormatado = acumulado?.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });

          return `${valorFormatado} (Acumulado: ${acumuladoFormatado})`;
        },
      },
    },
    legend: {
      position: "top",
    },
  };

  return (
    <div className="min-h-[326px]">
      {loading ? (
        <div className="flex justify-center items-center">
          <Loading />
        </div>
      ) : todosZerados ? (
        <p className="text-muted-foreground text-center">
          Não há dados disponíveis para exibir o gráfico.
        </p>
      ) : (
        <Chart options={options} series={series} type="line" height={310} />
      )}
    </div>
  );
};

export default DashboardLineChart;
